import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import PlusIcon from "../../../../assets/images/icons/PlusIcon.tsx";
import PriceCheckIcon from "../../../../assets/images/icons/PriceCheckIcon.tsx";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../constants/billing.ts";
import { useApiLimitStatus } from "../../../../hooks/useApiLimitStatus.tsx";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import BriaButton, { ButtonTypes } from "../../../common/BriaButton/BriaButton.tsx";
import BriaTooltip from "../../../common/BriaTooltip/BriaTooltip.tsx";
import LoadingPlaceholder from "../../../common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import { usePricingFeatureContent } from "../hooks/usePricingFeatureContent.ts";
import styles from "./PricingCard.module.scss";

export interface IPricingCards {
	title: string;
	description: string;
	subDescription: string;
	price: {
		title: string;
		description?: string;
	};
	buttonText: string;
	className?: string;
	buttonType?: ButtonTypes;
	planKey?: string;
	onFeatureClick?: (featureId: string | null) => void;
	selectedFeature?: string | null;
	features?: any;
	getFeatureContent?: (planKey: string, featureId: string) => any;
	maxExpandedHeight?: number;
	featureContentRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
	featureMinHeights?: { [key: string]: number };
	onButtonClick?: () => void;
}

const PricingCard = ({
	buttonType = "tertiaryMedium",
	className,
	planKey,
	onFeatureClick,
	selectedFeature,
	features,
	getFeatureContent,
	maxExpandedHeight,
	featureContentRefs,
	featureMinHeights,
	onButtonClick,
	...pricingCard
}: IPricingCards) => {
	const { t } = useTranslation("translation", { keyPrefix: "pricingFlow" });

	const { authStore } = useAppStore();
	const { isUserCreatedBeforeGrowthDate } = useApiLimitStatus();
	const { expandedFeatures } = usePricingFeatureContent();
	const canManageBilling = !!authStore.user?.isSuperAdminOrAdminOrOwner();
	const billingDisabledTooltip = !canManageBilling ? t("contactAdmins") : undefined;

	const isLoading = authStore.isLoadingUserOrganizations || authStore.isLoadingOrgSubscriptions;
	const currentPlanName = authStore.orgSubscription?.plan_name.toLowerCase();

	const getFeatureTitle = (content: any) => {
		if (typeof content.title === "string") {
			const titleContent = (
				<Box
					className={clsx(styles.titleWithIcon, {
						[styles.tooltip]: content.tooltip,
					})}
				>
					{content.title === "all" ? <PriceCheckIcon /> : <span>{content.title}</span>}
				</Box>
			);

			return content.tooltip ? (
				<BriaTooltip title={content.tooltip} className={styles.tooltipWrapper} displayOn="hover" mode="dark">
					{titleContent}
				</BriaTooltip>
			) : (
				titleContent
			);
		} else if (typeof content.title === "object") {
			return (
				<ul className={styles.featureTitlesContainer}>
					{content.features.map((feature: any, index: number) => (
						<li key={index}>{feature.title}</li>
					))}
				</ul>
			);
		}

		return null;
	};

	const getCTAButtonText = (buttonText: string, planKey?: string) => {
		// PAYG (selfService/starter) card logic
		if (planKey === "selfService") {
			if (currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name) {
				return authStore.orgSubscription?.cancels_on ? t("renewSubscription") : t("manageSubscription");
			} else if (currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
				return null;
			}
		}

		// Custom plan (briaService) card logic
		if (planKey === "briaService") {
			if (currentPlanName === ORG_SUBSCRIPTION_PLANS.business.name) {
				return authStore.orgSubscription?.cancels_on ? t("renewSubscription") : t("managePlan");
			}
		}

		if (planKey === "enterprise") {
			if (currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
				return authStore.orgSubscription?.cancels_on ? t("renewSubscription") : t("managePlan");
			}
		}

		return buttonText;
	};

	const shouldShowButton = (planKey?: string) => {
		// Free Trial: hide button if user has paid plan
		if (
			planKey === "freeTrial" &&
			(currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name ||
				currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name ||
				currentPlanName === ORG_SUBSCRIPTION_PLANS.business.name)
		) {
			return false;
		}

		// PAYG: hide button if user has custom plan
		if (
			planKey === "selfService" &&
			(currentPlanName === ORG_SUBSCRIPTION_PLANS.business.name ||
				currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name)
		) {
			return false;
		}

		return true;
	};

	const getButtonDisabled = (planKey?: string) => {
		if (authStore.orgSubscription?.is_marketplace) {
			return true;
		}
		// Free Trial: disable if user is already on free plan
		if (planKey === "freeTrial" && currentPlanName === ORG_SUBSCRIPTION_PLANS.free.name) {
			return true;
		}

		return false;
	};

	const handleCurrentPlan = () => {
		if (
			(planKey === "briaService" && currentPlanName === ORG_SUBSCRIPTION_PLANS.business.name) ||
			(planKey === "enterprise" && currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) ||
			(planKey === "selfService" && currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name) ||
			(planKey === "freeTrial" &&
				isUserCreatedBeforeGrowthDate &&
				currentPlanName === ORG_SUBSCRIPTION_PLANS.free.name)
		) {
			return t("yourCurrentPlan");
		}

		return null;
	};

	return (
		<Box className={clsx(styles.pricingCardContainer, className)}>
			<Box className={styles.currentPlan}>
				{<Typography className={styles.title}>{handleCurrentPlan()}</Typography>}
			</Box>
			<Box className={styles.pricingCardHeader}>
				<Typography className={styles.title}>{pricingCard.title}</Typography>
				<Typography className={styles.description}>{pricingCard.description}</Typography>
				<Typography className={styles.subDescription}>{pricingCard.subDescription}</Typography>
				<Box className={styles.buttonContainer}>
					{shouldShowButton(planKey) && !isLoading ? (
						<BriaButton
							className={clsx(styles.actionButton, {
								[styles.primary]: buttonType === "primaryMedium",
								[styles.disabled]: getButtonDisabled(planKey),
							})}
							buttonType={buttonType}
							disabledTooltip={billingDisabledTooltip}
							onClick={() => {
								if (!canManageBilling) return;
								onButtonClick?.();
							}}
							disabled={!canManageBilling || getButtonDisabled(planKey)}
						>
							{getCTAButtonText(pricingCard.buttonText, planKey)}
						</BriaButton>
					) : (
						<LoadingPlaceholder isLoading={isLoading} size={20} />
					)}
				</Box>
			</Box>

			{features && planKey && getFeatureContent && (
				<Box className={styles.featuresList}>
					{Object.entries(features).map(([featureId, _]: [string, any]) => {
						const content = getFeatureContent(planKey, featureId);
						const isExpanded = selectedFeature === featureId;
						const minHeight = featureMinHeights?.[featureId] || content.minHeight || 48;

						return (
							<Box
								key={featureId}
								className={clsx(styles.featureItem, {
									[styles.hidden]: content.title === "",
								})}
								style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
							>
								{!isExpanded && (
									<Box
										className={clsx(styles.featureHeader, {
											[styles.expanded]: isExpanded,
											[styles.fixedHeader]:
												!content.features?.length || !expandedFeatures.includes(featureId),
											[styles.currentPlanHeader]: handleCurrentPlan() !== null,
										})}
										onClick={() => {
											if (!content.features?.length || !expandedFeatures.includes(featureId)) {
												return;
											}
											onFeatureClick?.(isExpanded ? null : featureId);
										}}
									>
										<Typography className={styles.featureTitle}>
											{getFeatureTitle(content)}
										</Typography>
									</Box>
								)}
								{content.features?.length && expandedFeatures.includes(featureId) && (
									<Box
										className={clsx(styles.expandIcon, {
											[styles.expanded]: isExpanded,
										})}
										onClick={() => onFeatureClick?.(isExpanded ? null : featureId)}
									>
										<PlusIcon />
									</Box>
								)}

								{isExpanded && (
									<Box
										className={clsx(styles.featureContent, {
											[styles.expandable]: content.features?.length,
										})}
										ref={(el) => {
											if (planKey && featureContentRefs) {
												featureContentRefs.current[`${planKey}-${featureId}`] =
													el as HTMLDivElement;
											}
										}}
										style={{
											minHeight:
												maxExpandedHeight &&
												maxExpandedHeight > 0 &&
												selectedFeature === featureId
													? `${maxExpandedHeight}px`
													: "auto",
										}}
									>
										{typeof content.title === "string" && (
											<Typography className={styles.featureContentTitle}>
												{getFeatureTitle(content)}
											</Typography>
										)}
										{content.features?.map((featureGroup: any, index: number) => (
											<Box key={index} className={styles.featureGroup}>
												{featureGroup.title && (
													<Typography className={styles.featureGroupTitle}>
														{featureGroup.title}
													</Typography>
												)}
												<Box className={styles.featureItems}>
													{featureGroup.items?.map((item: string, itemIndex: number) => (
														<Box key={itemIndex} className={styles.featureItemText}>
															<Typography dangerouslySetInnerHTML={{ __html: item }} />
														</Box>
													))}
												</Box>
											</Box>
										))}
									</Box>
								)}
							</Box>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

export default observer(PricingCard);
